import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { toast } from 'react-toastify';
import { isViewingConversation } from '@utils/activeConversation';

/**
 * App-wide real-time in-app notifications for stylists / business owners: one socket that
 * pops a toast on the server `notification` event (new booking, pending approval, …).
 * The persisted Notification (Inbox) is the fallback when the user is offline.
 *
 * id-matching: notifications are keyed to the Staff._id. We connect WITH the handshake JWT so
 * the server can verify identity and derive the recipient id server-side — a stylist token's
 * `_id` IS the Staff id, and a business token carries the linked `staffId` (the server maps
 * business → staffId). The server IGNORES any client-claimed id, so a token is REQUIRED to
 * receive notifications (this closes an unauthenticated-subscription hole).
 *
 * It also registers for chat (`register`) so a customer's message surfaces wherever the
 * stylist is, not only while a conversation is open. That's safe because the server's chat
 * registry holds a SET of sockets per user — it used to keep just one, so registering here
 * would have evicted the conversation view's socket and broken chat delivery outright.
 */
type AppNotification = { content?: string; type?: string; link?: string };
type ChatMessage = { sender?: { _id?: string }; content?: string; media?: string };

const MSG_PREVIEW_MAX = 60;

const socketUrl = (): string =>
  (import.meta.env.VITE_CHAT_SOCKET_URL as string) || 'https://api.hkstylist.com';

// A new booking is the one notification a stylist actually acts on — don't let it look like
// every other notice. 'service' covers the reservation notifications; the rest stay neutral.
const notify = (n: AppNotification): void => {
  const content = n.content as string;
  if (n.type === 'service') toast.success(content);
  else toast.info(content);
};

/**
 * The id this account receives CHAT on. Unlike register:notify (derived server-side from the
 * token), chat's `register` takes a client-supplied id, so we mirror what the conversation
 * view sends: a business account chats as its linked staffId, everyone else as their _id.
 */
const getChatId = (): string | null => {
  try {
    const raw = sessionStorage.getItem('impersonated_user') || localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    if (!u) return null;
    return (u.role === 'business' ? u.staffId : u._id) || null;
  } catch {
    return null;
  }
};

const chatPreview = (m: ChatMessage): string => {
  const body = (m.content || '').trim();
  if (!body) return '💬 收到一張相片';
  return `💬 ${body.length > MSG_PREVIEW_MAX ? `${body.slice(0, MSG_PREVIEW_MAX)}…` : body}`;
};

const GlobalNotifications: React.FC = () => {
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    let socket: Socket | null = null;
    let retries = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    // Connect once a session is available. Poll so a login that happens AFTER mount
    // (SPA nav, no reload) still gets wired up.
    const tryConnect = () => {
      if (socket) return;
      // Only a token is required — the server derives the recipient (business → staffId) from
      // it. Don't gate on a locally-derived id: a business login whose cached `user` hasn't got
      // `staffId` yet would never connect, even though the token carries it.
      const readToken = () =>
        sessionStorage.getItem('impersonation_token') || localStorage.getItem('token') || undefined;
      if (!readToken()) return;

      // `auth` MUST be a callback, not a captured value: the JWT expires in ~30m and is rotated,
      // while this socket can outlive it (a BE deploy reconnects every client). The callback
      // re-runs on every connection attempt, so a retry picks up the fresh token.
      const s = io(socketUrl(), {
        auth: (cb: (data: { token?: string }) => void) => cb({ token: readToken() }),
        reconnectionDelayMax: 30000,
      });
      socket = s;
      socketRef.current = s;

      const register = () => {
        s.emit('register:notify');
        // Also join the chat registry so messages reach us anywhere in the app. Additive
        // server-side, so this never displaces an open conversation's socket.
        const chatId = getChatId();
        if (chatId) s.emit('register', chatId);
      };
      s.on('connect', () => {
        retries = 0;
        register(); // (re)register on every (re)connect
      });
      // A handshake rejected by server middleware (e.g. an expired JWT) is FATAL to socket.io:
      // it sets `active = false` and never retries on its own. Reconnect ourselves so the auth
      // callback re-reads the rotated token — otherwise notifications stay dead until a reload.
      s.on('connect_error', () => {
        if (s.active) return; // transport blip → socket.io retries by itself
        retries += 1;
        retryTimer = setTimeout(() => s.connect(), Math.min(30000, 2000 * 2 ** (retries - 1)));
      });
      // A customer's message, surfaced wherever the stylist is — except in the conversation
      // they're already looking at, where they watch it arrive inline.
      s.on('receiveMessage', (m: ChatMessage) => {
        const senderId = m?.sender?._id;
        if (!senderId || isViewingConversation(senderId)) return;
        toast.info(chatPreview(m));
      });
      s.on('notification', (n: AppNotification) => {
        if (n?.content) notify(n);
      });
    };

    tryConnect();
    const iv = window.setInterval(tryConnect, 3000);
    return () => {
      window.clearInterval(iv);
      if (retryTimer) clearTimeout(retryTimer);
      if (socket) {
        socket.off();
        socket.disconnect();
      }
      socketRef.current = null;
    };
  }, []);

  return null;
};

export default GlobalNotifications;
